import React, {useEffect, useRef, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {BaseFullPage} from '../base';
import {SplitHeroLayout} from '../../components';
import {isRestErrorWithStatus, useParamState, useTranslate} from '../../hooks';
import {useSigningStatus} from '../../hooks/useSigningStatus';
import {SigningView} from './SigningView';
import {Typography} from '@mui/material';
import {getOrCreateBft, BftResult} from '../../services/bft';
import {WrongBrowserError} from '../error/WrongBrowserError';
import signHeroImage from 'assets/images/backgrounds/forest.png';

type SigningViewState = 'signing' | 'completed';

const safeHttpUrl = (url?: string): string | undefined => {
  if (!url) return undefined;
  try {
    const {protocol} = new URL(url);
    return protocol === 'http:' || protocol === 'https:' ? url : undefined;
  } catch {
    return undefined;
  }
};

export const SigningMBIDPage = () => {
  const navigate = useNavigate();
  const t = useTranslate();
  const [{id, signer}] = useParamState<{
    id?: string;
    signer?: string;
  }>();

  useEffect(() => {
    if (!id || !signer) {
      navigate('/login/error');
    }
  }, [id, signer, navigate]);

  const [view, setView] = useState<SigningViewState>('signing');
  const [bftState, setBftState] = useState<BftResult | undefined>();
  const [isSessionInitialized, setIsSessionInitialized] = useState(false);
  const [initAutoStartToken, setInitAutoStartToken] = useState<string>('');
  const [lastQrCode, setLastQrCode] = useState<string | undefined>(undefined);
  const initCalledForRef = useRef<string | null>(null);
  const bft = bftState?.bft.value;
  const pollingEnabled = view === 'signing' && isSessionInitialized;

  const {query, cancel, initializeSession, initializeSessionAsync} =
    useSigningStatus(id, signer, pollingEnabled, bft);
  const rawStatus = query.data?.status;
  const autoStartToken = query.data?.autoStartToken ?? initAutoStartToken;
  if (query.data?.qrCode && query.data.qrCode !== lastQrCode) {
    setLastQrCode(query.data.qrCode); // eslint-disable-line react-hooks/set-state-in-render
  }
  const qrCode = query.data?.qrCode ?? lastQrCode;
  const redirectUrl = safeHttpUrl(query.data?.redirectUrl);
  const cancelRedirectUrl = safeHttpUrl(query.data?.cancelRedirectUrl);

  const queryError = query.error;
  const initError = initializeSession?.error;
  const isWrongBrowserError =
    isRestErrorWithStatus(queryError, [400]) ||
    isRestErrorWithStatus(initError, [400]);

  const otherError =
    (!isWrongBrowserError ? queryError : null) ??
    (!isWrongBrowserError ? initError : null) ??
    cancel?.error;
  if (otherError) throw otherError;

  useEffect(() => {
    if (!signer) return;
    setBftState(getOrCreateBft(`signing-${signer}`)); // eslint-disable-line react-hooks/set-state-in-effect
    setIsSessionInitialized(false); // eslint-disable-line react-hooks/set-state-in-effect
    setInitAutoStartToken(''); // eslint-disable-line react-hooks/set-state-in-effect
    setLastQrCode(undefined); // eslint-disable-line react-hooks/set-state-in-effect
    initCalledForRef.current = null;
  }, [signer]);

  useEffect(() => {
    if (!bftState || !signer) return;
    if (isSessionInitialized) return;
    if (initCalledForRef.current === signer) return;
    initCalledForRef.current = signer;

    (async () => {
      try {
        const result = await initializeSessionAsync();
        if (initCalledForRef.current !== signer) return;
        if (result?.autoStartToken) {
          setInitAutoStartToken(result.autoStartToken);
        }
        setIsSessionInitialized(true);
      } catch {
        if (initCalledForRef.current === signer) {
          initCalledForRef.current = null;
        }
      }
    })();
  }, [bftState, isSessionInitialized, signer, initializeSessionAsync]);

  useEffect(() => {
    if (!rawStatus) return;
    if (rawStatus === 'COMPLETED') {
      if (signer) localStorage.removeItem(`bft-signing-${signer}`);
      if (redirectUrl) {
        window.location.replace(
          `${redirectUrl}?status=SUCCESSFUL&id=${id}&signer=${signer}`,
        );
      } else {
        setView('completed'); // eslint-disable-line react-hooks/set-state-in-effect
      }
    } else if (
      rawStatus === 'REJECTED' ||
      rawStatus === 'EXPIRED' ||
      rawStatus === 'UNKNOWN' ||
      rawStatus === 'SIGNER_MISMATCH' ||
      rawStatus === 'CANCELLED_VIA_API'
    ) {
      if (signer) localStorage.removeItem(`bft-signing-${signer}`);
      window.location.replace(
        cancelRedirectUrl
          ? `${cancelRedirectUrl}?status=${rawStatus}&id=${id}&signer=${signer}`
          : redirectUrl
            ? `${redirectUrl}?status=${rawStatus}&id=${id}&signer=${signer}`
            : `/login/error`,
      );
    }
  }, [rawStatus, redirectUrl, cancelRedirectUrl, id, signer]);

  const handleCancel = async () => {
    if (cancel.isPending) return;
    await cancel.mutateAsync();
  };

  if (isWrongBrowserError) {
    return <WrongBrowserError />;
  }

  const page = (
    <BaseFullPage id={'signing_mbid'}>
      {view === 'signing' && (
        <SigningView
          autoStartToken={autoStartToken}
          onCancel={handleCancel}
          qrCode={qrCode}
          isLoading={query.isLoading}
          hintCode={query.data?.hintCode}
          expiresAt={query.data?.expiresAt}
        />
      )}
      {view === 'completed' && (
        <Typography variant={'h2'} textAlign={'center'}>
          {t('signature.bankid.completed')}
        </Typography>
      )}
    </BaseFullPage>
  );

  // Figma's desktop Sign page (only while actually signing, not the
  // "completed" state) shows the same full-height brand image as the
  // pre-review page — see DocumentsViewer.
  return view === 'signing' ? (
    <SplitHeroLayout image={signHeroImage}>{page}</SplitHeroLayout>
  ) : (
    page
  );
};
